import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { DownloadSimple } from "@phosphor-icons/react";

/**
 * ProductDetails — Per Word doc v2:
 * - Division 1: Brake Components Division (6 items with images; Brake Rotors + Universal Joint Cross removed)
 * - Division 2: Rane Other Parts & Components (engine components, 6 items)
 * - Division 3: Industrial & Off-Road Tyres
 * - Division 4: Wheel Alignment, Balancing & Tyre Fitting Machines (incl. Tyre Fitting Accessories)
 * - Catalogue downloads: Rane PDF only (Sinobrake & 3 catalogue images removed)
 * - No manufacturer/supplier links anywhere
 */

const brakeItems = [
  { name: "Commercial Vehicle Brake Pads", img: "/products/v2/cv-brake-pads.png", text: "Heavy-duty pads engineered for commercial fleets — long service life and consistent stopping performance." },
  { name: "Disc Pads", img: "/products/v2/disc-pads.png", text: "Wide passenger and 2-wheeler disc-pad range engineered for low NVH and consistent braking feel." },
  { name: "Brake Linings", img: "/products/v2/brake-linings.png", text: "Asbestos-free brake linings developed in technical collaboration with Nisshinbo Brakes Inc., Japan — applicable across every segment of the automobile industry." },
  { name: "Railway Brake Blocks", img: "/products/v2/railway-brake-blocks.png", text: "Leading supplier of composite brake blocks to Indian Railways, with proven supply experience to Sri Lankan Railways too." },
  { name: "Sintered Brake Pads", img: "/products/v2/sintered-brake-pads.png", text: "Sintered friction technology for heavy-duty applications demanding high temperature and pressure tolerance." },
  { name: "Clutch Facings", img: "/products/v2/clutch-facings.png", text: "Genuine clutch-facing rings produced to OEM specification for reliable transmission engagement." },
];

const engineItems = [
  { name: "Engine Valves", text: "Kar Valves — leading supplier of high-quality, precision engine valves in line with OEM standards across 4-wheelers, tractors and industrial applications." },
  { name: "Water Pump", text: "Rane water pumps with high-quality bearings and seals for quiet, efficient operation." },
  { name: "Engine Mountings", text: "Wide range of engine mounts with high-quality rubber and precisely manufactured parts for vibration-damping performance." },
  { name: "Suspension Bush Kit", text: "Suspension bushes that deliver superior bonding through high-quality rubber and precision manufacturing." },
  { name: "King Pin", text: "King-pin sets and serviceable parts with chrome-steel kingpins, adequately hardened for strength." },
  { name: "Gaskets", text: "Full kit, half kit, CHG and multi-layer steel gaskets covering a wide range of 4-wheeler applications." },
];

const tyreItems = [
  { name: "Solid Resilient Tyres", text: "High-quality natural rubber compounds for enhanced traction and reduced fuel consumption." },
  { name: "Press-On Band Tyres", text: "Exceptional price-performance ratio for material-handling fleets — reinforced sidewalls for impact protection." },
  { name: "Pneumatic Tyres", text: "Low rolling resistance and enhanced traction across every material-handling application." },
  { name: "Polyurethane Wheels", text: "Engineered for warehouse and factory floors — long-life polyurethane tread on rugged hubs." },
  { name: "Assemblies", text: "Ready-fit tyre-and-wheel assemblies for forklifts and material-handling equipment." },
  { name: "Wheels & Rims", text: "Industrial-grade wheels and rims compatible with our full tyre programme." },
];

const equipmentItems = [
  { name: "Wheel Alignment Machine", text: "World-class 3D aligner (WA400AT) — HD cameras, 4 strobes, universal clamps and auto-tracking." },
  { name: "Wheel Balancing Machine", text: "Professional balancing equipment for cars, LCVs and commercial vehicles." },
  { name: "Tyre Fitting Machine", text: "Heavy-duty fitting machines for fast, safe tyre changes across passenger and commercial fitments." },
  { name: "Tyre Fitting Accessories", text: "Mpover Premium Tyre Mounting Paste — ISO 9001:2015 certified, safe for daily workshop use." },
];

export default function ProductDetails() {
  return (
    <>
      {/* BRAKE COMPONENTS DIVISION */}
      <section id="brake-division" data-testid="brake-division" className="relative py-16 md:py-24 bg-white border-y border-[#D5DEE8]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Header
            overline="Division 01"
            title="Brake Components"
            italic="Division"
            sub="Asbestos-free brake linings, disc pads, clutch facings and friction-critical components — engineered with Rane Group and applied across every segment of the automobile industry."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {brakeItems.map((it, idx) => (
              <ProductCellWithImage key={it.name} idx={idx} item={it} />
            ))}
          </div>
        </div>
      </section>

      {/* RANE OTHER PARTS & COMPONENTS */}
      <section id="engine-division" data-testid="engine-division" className="relative py-16 md:py-24 bg-[#D6E2F0]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Header
            overline="Division 02"
            title="Rane Other Parts &"
            italic="Components"
            sub="A complete aftermarket range for the engine, gear pumps — valves, pumps, gaskets, mounts and steering hardware."
          />

          <div className="grid grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="col-span-12 lg:col-span-5">
              <div className="bg-white border border-[#D5DEE8] p-3 md:p-4">
                <img
                  src="/products/v2/engine-parts.png"
                  alt="Rane other parts &amp; engine components"
                  className="w-full h-auto max-h-[720px] object-contain"
                />
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {engineItems.map((it, idx) => (
                <ProductCell key={it.name} idx={idx} item={it} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TYRES */}
      <section id="tyres-division" data-testid="tyres-division" className="relative py-16 md:py-24 bg-white border-y border-[#D5DEE8]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Header
            overline="Division 03"
            title="Industrial &"
            italic="Off-Road Tyres"
            sub="The complete spectrum of solid resilient, press-on band, pneumatic and polyurethane tyres for every Material-Handling application — plus all leading OTR &amp; agri tyres including Michelin, Bridgestone, Yokohama and top Indian / Chinese brands."
          />

          <div className="bg-[#D6E2F0] border border-[#D5DEE8] p-3 md:p-5 mb-8">
            <img
              src="/products/v2/image11.png"
              alt="Industrial and off-road tyre range"
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-10">
            {tyreItems.map((it, idx) => (
              <ProductCell key={it.name} idx={idx} item={it} />
            ))}
          </div>

          <div className="bg-[#D6E2F0] border border-[#D5DEE8] p-5 md:p-7">
            <div className="overline-maroon mb-3">Off-Road Tyres · OTR / Agri</div>
            <p className="text-[#1F2D40] text-base md:text-lg leading-relaxed">
              We offer all leading-brand OTR / Agri tyres — Michelin,
              Bridgestone, Yokohama — and top-performing Indian &amp; Chinese
              brands. Specify your application and we'll match the right rubber.
            </p>
          </div>

          <a
            href="/products/solido-solid-tyres-catalogue.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="solido-catalog-download"
            className="group mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 bg-[#1E3A5F] text-[#D6E2F0] p-6 md:p-8 hover:bg-[#15273F] transition-colors"
          >
            <div className="shrink-0 w-14 h-14 flex items-center justify-center border border-[#D4AF37]/50 text-[#D4AF37]">
              <DownloadSimple size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-1">Catalogue · PDF</div>
              <div className="font-serif-display text-xl md:text-2xl">SOLIDO — Solid Tyres Catalogue</div>
              <div className="text-sm md:text-base text-[#D6E2F0]/75 mt-1">Full solid tyre reference · Click to view or download</div>
            </div>
          </a>
        </div>
      </section>

      {/* WORKSHOP EQUIPMENT */}
      <section id="equipment-division" data-testid="equipment-division" className="relative py-16 md:py-24 bg-[#D6E2F0]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Header
            overline="Division 04"
            title="Wheel Alignment, Balancing &"
            italic="Tyre Fitting Machines"
            sub="World-class wheel alignment machines, balancing machines, tyre fitting machines and accessories for modern workshops."
          />

          <div className="grid grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="col-span-12 lg:col-span-5">
              <div className="bg-white border border-[#D5DEE8] p-3 md:p-4">
                <img
                  src="/products/v2/wheel-balancer.png"
                  alt="Wheel alignment, balancing and tyre fitting equipment"
                  className="w-full h-auto max-h-[720px] object-contain"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {equipmentItems.map((it, idx) => (
                <ProductCell key={it.name} idx={idx} item={it} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATALOGUE DOWNLOAD — Rane only */}
      <section id="catalogues" data-testid="catalogues-section" className="relative py-16 md:py-24 bg-white border-y border-[#D5DEE8]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <Header
            overline="Download"
            title="Product"
            italic="Catalogue"
            sub="Download the official Rane Exports product catalogue — your complete reference for OE part numbers, fitment and specifications."
          />

          <a
            href="/products/rane-catalog-2026.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="rane-catalog-download"
            className="group flex flex-col sm:flex-row items-start sm:items-center gap-5 bg-[#1E3A5F] text-[#D6E2F0] p-6 md:p-8 hover:bg-[#15273F] transition-colors max-w-3xl mx-auto"
          >
            <div className="shrink-0 w-14 h-14 flex items-center justify-center bg-[#D4AF37] text-[#0F1E33]">
              <DownloadSimple size={26} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-1">Featured · PDF</div>
              <div className="font-serif-display text-xl md:text-2xl">Rane Exports Product Catalogue — April 2026</div>
              <div className="text-sm md:text-base text-[#D6E2F0]/75 mt-1">Full product reference · Click to view or download</div>
            </div>
          </a>

          <div className="mt-10 text-center">
            <Link
              to="/quote"
              data-testid="catalogue-quote-cta"
              className="inline-flex items-center gap-3 px-7 py-3.5 bg-[#6B1F2C] text-[#D4AF37] hover:bg-[#4A1620] transition-colors tracking-wide font-semibold"
            >
              Request a Quote for Any Item
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Header({ overline, title, italic, sub }) {
  return (
    <div className="mb-10 md:mb-12">
      <p className="overline-maroon mb-3">{overline}</p>
      <h3 className="font-serif-display font-light text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#0F1E33] leading-[1.05]">
        {title} <span className="italic text-[#1E3A5F]">{italic}</span>
      </h3>
      <div className="hairline-gold w-20 mt-5" />
      <p
        className="mt-5 max-w-3xl text-[#1F2D40] text-base md:text-lg lg:text-xl leading-relaxed"
        dangerouslySetInnerHTML={{ __html: sub }}
      />
    </div>
  );
}

function ProductCellWithImage({ item, idx }) {
  return (
    <motion.div
      data-testid={`brake-item-${idx}`}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: idx * 0.05 }}
      className="bg-white border border-[#D5DEE8] hover:border-[#B8860B] transition-colors overflow-hidden flex flex-col"
    >
      <div className="w-full h-48 md:h-56 bg-[#D6E2F0] flex items-center justify-center p-3">
        <img src={item.img} alt={item.name} loading="lazy" className="max-h-full w-auto object-contain" />
      </div>
      <div className="p-5 md:p-6">
        <h4 className="font-serif-display text-xl md:text-2xl text-[#0F1E33] tracking-tight">{item.name}</h4>
        <p className="mt-2 text-base md:text-lg text-[#1F2D40] leading-relaxed">{item.text}</p>
      </div>
    </motion.div>
  );
}

function ProductCell({ item, idx }) {
  return (
    <motion.div
      data-testid={`product-cell-${idx}`}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: idx * 0.04 }}
      className="bg-white border border-[#D5DEE8] hover:border-[#B8860B] transition-colors p-5 md:p-6"
    >
      <h4 className="font-serif-display text-xl md:text-2xl text-[#0F1E33] tracking-tight">{item.name}</h4>
      <p className="mt-2 text-base md:text-lg text-[#1F2D40] leading-relaxed">{item.text}</p>
    </motion.div>
  );
}
